import {
  AnonymousProfileConfig,
  BundleConfig,
  ProfileConfig,
  SecurityOptionsConfig,
  SpecialProfileConfig,
  TapConfig,
  UserConfig,
  VariableConfig,
} from '@iotize/tap/config/schema/v1';
import { SPECIAL_USERS } from '@iotize/tap/configurator';
import { BehaviorSubject } from 'rxjs';
import { DEFAULT_SESSION_LIFETIME } from './config';
import { TapServerError } from './tap-server-error';

type ProfileConfigKind =
  | SpecialProfileConfig
  | AnonymousProfileConfig
  | ProfileConfig;

export function resolveProfile(p: ProfileConfigKind): UserConfig {
  const resolvedProfile = p as UserConfig;
  if (!('id' in resolvedProfile)) {
    const specialUser = SPECIAL_USERS.find((sp) => sp.username === p.username);
    if (!specialUser) {
      throw new Error(
        'Cannot resolve profile id from ' + resolvedProfile.username
      );
    }
    resolvedProfile.id = specialUser.id;
  }
  if (!resolvedProfile.lifeTime) {
    resolvedProfile.lifeTime = DEFAULT_SESSION_LIFETIME;
  }
  return resolvedProfile;
}

/**
 * TODO merge with TapConfiguratorConfigBuilder
 */
export class ConfigExplorer {
  private _configChange: BehaviorSubject<TapConfig>;
  private _variablesWithoutBundles: VariableConfig[] = [];

  public get config(): TapConfig {
    return this._configChange.value;
  }

  get profiles(): ProfileConfig[] {
    if (!this.data.profiles) {
      this.data.profiles = [];
    }
    return this.data.profiles as ProfileConfig[];
  }

  get data() {
    if (!this.config.data) {
      this.config.data = {};
    }
    return this.config.data;
  }

  public set config(config: TapConfig) {
    this._configChange.next(this._sanitizeConfig(config));
  }

  public get configChange() {
    return this._configChange.asObservable();
  }

  public get security(): SecurityOptionsConfig {
    if (!this.config.tap) {
      this.config.tap = {};
    }
    if (!this.config.tap.security) {
      this.config.tap.security = {};
    }
    return this.config.tap.security;
  }

  constructor(config: TapConfig) {
    this._configChange = new BehaviorSubject(this._sanitizeConfig(config));
  }

  get variables() {
    const variables: VariableConfig[] = [];
    this.bundles.forEach((b) => variables.push(...(b.variables || [])));
    variables.push(...this._variablesWithoutBundles);
    return variables;
  }

  get variablesWithMeta() {
    const variables: {
      variable: VariableConfig;
      bundle: BundleConfig | undefined;
    }[] = [];
    this.bundles.forEach((bundle) => {
      if (bundle.variables) {
        variables.push(
          ...bundle.variables?.map((variable) => ({
            variable,
            bundle,
          }))
        );
      }
    });
    variables.push(
      ...this._variablesWithoutBundles.map((variable) => {
        return {
          variable,
          bundle: undefined,
        };
      })
    );
    return variables;
  }

  // get config() {
  //     return this.schema.config;
  // }

  get bundles() {
    return this.config.data?.bundles || [];
  }

  reset() {
    this.config.data = {};
  }

  findProfileById(id: number): UserConfig | undefined {
    return this.allProfiles.find((p) => p.id === id);
  }

  findUserOrProfileById(id: number): UserConfig | ProfileConfig | undefined {
    return this.allUserOrProfiles.find((p) => p.id === id);
  }

  get allUserOrProfiles(): (UserConfig | ProfileConfig)[] {
    return this.allProfiles.reduce((prev, p) => {
      prev.push(p);
      if (p.users) {
        prev.push(...p.users);
      }
      return prev;
    }, <(UserConfig | ProfileConfig)[]>[]);
  }

  get allProfiles(): ProfileConfig[] {
    const configProfiles = this.config.data?.profiles || [];
    const profiles = [...configProfiles];
    SPECIAL_USERS.forEach((sp) => {
      if (!profiles.find((p) => p.username === sp.username)) {
        profiles.push(sp);
      }
    });
    return profiles.map(resolveProfile);
  }

  changeUserAlias(id: number, newGroupId: number) {
    const newProfileContainer = this.profiles.find((p) => p.id === newGroupId);
    if (!newProfileContainer) {
      console.warn(
        `Cannot move user "${id}" to group "${newGroupId}" as this profile does not exist`
      );
      throw TapServerError.badRequest();
    }
    for (const profile of this.profiles) {
      if (profile.id === id) {
        const profileIndex = this.profiles.indexOf(profile);
        if (profileIndex !== -1) {
          this.profiles.splice(profileIndex, 1);
          if (!newProfileContainer.users) {
            newProfileContainer.users = [];
          }
          newProfileContainer.users.push(profile);
          return;
        }
      } else if (!profile.users) {
        continue;
      } else {
        const userIndex = profile.users.findIndex(
          (u: UserConfig) => u.id === id
        );
        if (userIndex !== -1) {
          const profileId = profile.id;
          if (profileId !== newGroupId) {
            const [profileToMove] = profile.users.splice(userIndex, 1);
            if (!newProfileContainer.users) {
              newProfileContainer.users = [];
            }
            newProfileContainer.users.push(profileToMove);
          }
          return;
        }
      }
    }
    console.warn(
      `Cannot move user "${id}" to group "${newGroupId}" as this user does not exist`
    );
    throw TapServerError.badRequest();
  }

  createProfile(id: number) {
    if (!this.data.profiles) {
      this.data.profiles = [];
    }
    this.data.profiles.push({
      id,
      username: '',
    });
  }

  createBundle(id: number) {
    if (!this.data.bundles) {
      this.data.bundles = [];
    }
    this.data.bundles.push({
      id,
    });
  }

  createVariable(variableId: number, metaSize: number) {
    if (this.variables.find((v) => v.id === variableId)) {
      console.warn(`variable with id ${variableId} already exist`);
      throw TapServerError.notAcceptable();
    }
    this._variablesWithoutBundles.push({
      id: variableId,
      address: 0,
      domain: 'TARGET',
      dataType: 'UINT32',
      metaDataLength: metaSize,
    });
  }

  getBundleById(id: number): BundleConfig {
    const bundle = this.bundles.find((b) => b.id === id);
    if (!bundle) {
      console.warn(`Cannot find existing bundle ${id}`);
      throw TapServerError.notFound();
    }
    return bundle;
  }

  getVariableById(variableId: number): VariableConfig {
    const variable = this.variables.find((v) => v.id === variableId);
    if (!variable) {
      throw TapServerError.notFound();
    }
    return variable;
  }

  getVariableWithMetaById(variableId: number) {
    const info = this.variablesWithMeta.find(
      (currInfo) => currInfo.variable.id === variableId
    );
    if (!info) {
      throw TapServerError.notFound();
    }
    return info;
  }

  changeVariableBundle(variableId: number, bundleId: number) {
    const bundle = this.getBundleById(bundleId);

    let variable: VariableConfig | undefined;
    const index = this._variablesWithoutBundles.findIndex(
      (v) => v.id === variableId
    );
    if (index >= 0) {
      variable = this._variablesWithoutBundles[index];
      this._variablesWithoutBundles.splice(index, 1);
    } else {
      for (const bundle of this.bundles) {
        const variables = bundle.variables;
        if (variables) {
          const index = variables.findIndex((v) => v.id === variableId);
          if (index >= 0) {
            variable = variables[index];
            variables.splice(index, 1);
          }
        }
      }
    }
    if (!variable) {
      console.warn(`Cannot find existing variable ${variableId}`);
      throw TapServerError.notFound();
    }
    if (!bundle.variables) {
      bundle.variables = [];
    }
    bundle.variables.push(variable);
  }

  private _sanitizeConfig(config: TapConfig): TapConfig {
    if (config.data?.profiles) {
      config.data.profiles = config.data.profiles.map((p) => resolveProfile(p));
    }
    return config;
  }
}
